import { Box, Button, Progress, useToast } from "@chakra-ui/react";
import { FiFlag } from "react-icons/fi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";
import { trackEvent } from "../../../shared/analytics";
import useStartReadingOrder from "../../../shared/hooks/useStartReadingOrder";
import { createGoal } from "../../../services/goalsApi";
import { getReadingForOrder } from "../../../services/readingOrdersApi";
import ReadingOrderCard from "../../browse/components/ReadingOrderCard";

/**
 * A reading order linked to a screen release: the usual browse card with a
 * "Start" or "Continue" action, plus the reader's progress bar.
 * Mirrors the mobile ReleaseReadingOrderCard component.
 */
const ReleaseReadingOrderCard = ({ readingOrder, release }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isPremium } = usePremium();
  const { startReadingOrder, isLoading: isStarting } = useStartReadingOrder();
  // "Read before it lands": a deadline goal pre-filled with the order and the
  // release date. One tap, no sheet. Only offered while the release is ahead.
  const canSetGoal = !!release?.isUpcoming && !!release?.releaseDate;
  const goalMutation = useMutation((replace) =>
    createGoal({
      type: "deadline",
      readingOrderId: readingOrder.id,
      endsOn: release.releaseDate,
      source: "film_release",
      sourceRefId: release.id,
      ...(replace ? { replace: true } : {}),
    })
  );

  const setGoal = (replace = false) => {
    if (!isPremium) {
      // Goals are Premium only: send free readers to the upgrade page.
      track("release_goal_upgrade_click");
      navigate("/go-premium");
      return;
    }
    track("release_goal_click");
    goalMutation
      .mutateAsync(replace)
      .then(() => {
        queryClient.invalidateQueries(["goals"]);
        toast({
          title: "Goal set.",
          description: `Finish ${readingOrder.title} before it lands. Track it on your profile.`,
          status: "success",
          duration: 6000,
          isClosable: true,
        });
      })
      .catch((error) => {
        if (error?.response?.status === 409 && !replace) {
          if (window.confirm("Replace your existing goal on this order?")) {
            setGoal(true);
          }
          return;
        }
        const data = error?.response?.data;
        const firstFieldError = data?.errors
          ? Object.values(data.errors).flat()[0]
          : null;
        toast({
          title: "Could not set the goal",
          description: firstFieldError || data?.message || "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  const readingQuery = useQuery(["readingForOrder", readingOrder.id], () =>
    getReadingForOrder(readingOrder.id)
  );
  const reading = readingQuery.data || null;
  const progress = Math.min(Math.max(Number(reading?.progress) || 0, 0), 100);

  const track = (event) =>
    trackEvent(event, {
      releaseId: release.id,
      readingOrderId: readingOrder.id,
    });

  const onAction = () => {
    if (isStarting) {
      return;
    }
    if (reading) {
      track("release_order_continue_click");
      navigate(`/reading/${reading.id}`, { state: { reading } });
      return;
    }
    track("release_order_start_click");
    startReadingOrder(readingOrder.id, readingOrder.franchise);
  };

  return (
    <Box position="relative" borderRadius="lg" overflow="hidden" mb="4">
      <ReadingOrderCard
        id={readingOrder.id}
        title={readingOrder.title}
        thumbnail={readingOrder.thumbnail}
        comicsCount={readingOrder.comicsCount}
        readersCount={readingOrder.readersCount ?? readingOrder.readingCount}
        years={readingOrder.years}
        premiumOnly={Boolean(
          readingOrder.paywallPremiumOnly ?? readingOrder.premiumOnly
        )}
        isPremium={isPremium}
        // Wait for the reading lookup so the label is right from the start.
        hideStartAction={readingQuery.isLoading}
        startActionLabel={reading ? "Continue" : "Start"}
        onClickStart={onAction}
        onClick={() => track("release_order_card_click")}
      />
      {!!reading && (
        <Progress
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          size="sm"
          height="3px"
          value={progress}
          aria-label={`${Math.round(progress)}% read`}
        />
      )}
      {canSetGoal && (
        <Button
          bg="gray.800"
          borderRadius="0"
          borderTop="1px solid"
          borderColor="gray.600"
          isLoading={goalMutation.isLoading}
          leftIcon={<FiFlag />}
          onClick={() => setGoal(false)}
          size="sm"
          variant="ghost"
          w="100%"
        >
          Read before it lands
        </Button>
      )}
    </Box>
  );
};

export default ReleaseReadingOrderCard;
